import React, { memo } from "react";

/**
 * @fileoverview ACTIVE COMPANY CARD ITEM COMPONENT
 * @active Used in current Next.js App Router routes
 * 
 * THIS IS THE CURRENT ACTIVE IMPLEMENTATION FOR INDIVIDUAL COMPANY CARDS.
 * It is used by the NewCompaniesCards component to render each company in the grid.
 * 
 * Key features:
 * - Displays company logo, name, and metadata
 * - Shows accelerator information
 * - Provides links to company details
 * 
 * CURRENT APP ROUTER COMPONENT HIERARCHY:
 * - page.tsx: Server component that fetches data
 *   └─ CompaniesWrapper.client.tsx: Client-side wrapper with "use client" directive
 *      └─ NewCompanies.tsx: Implements the view switching and data display
 *         └─ NewCompaniesCards: CARD VIEW implementation (when isCards=true)
 *            └─ CompanyNewCardItem (THIS FILE): Individual company card component
 * 
 * @see {@link src/components/companies/newCompaniesCards/index.tsx} - ACTIVE parent component
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/CompanyCardItemHeader.tsx} - Card header component
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/CompanyTagsItem.tsx} - Tags component
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/CompanyStageItem.tsx} - Stage component
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorCardView.tsx} - Accelerator component
 * @see {@link src/components/ui/cardCompanies/CompanyCard.jsx} - DEPRECATED implementation this replaces

 * @see {@link src/components/companies/newCompaniesCards/index.tsx} - Parent component that uses this
 * @see {@link src/components/companies/NewCompanies.tsx} - View switching component
 * @see {@link src/components/companies/CompaniesWrapper.client.tsx} - Client wrapper used in App Router
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - Current App Router page component
 * 
 * @note This component replaces the deprecated implementation in src/components/ui/cardCompanies/CompanyCard.jsx
 * @see {@link src/components/ui/cardCompanies/CompanyCard.jsx} - DEPRECATED implementation
 * @see {@link src/components/companies/companiesCard/CompaniesCard.jsx} - DEPRECATED container
 * @see {@link src/components/companies/Companies.tsx} - DEPRECATED page component
 */

import { Flex, Text } from "@chakra-ui/react";
import Link from "next/link";

import { CustomElement } from "@/components/ui/box/CustomElement";

import { AcceleratorCardView } from "../../companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorCardView";
import type { CompanyItem } from "../../companiesList/newCompaniesList/types";

import CompanyCardItemHeader from "./CompanyCardItemHeader";
import CompanyStageItem from "./CompanyStageItem";

/**
 * Props for the CompanyCardItem component
 */
interface CompanyCardItemProps {
  company: CompanyItem & {
    headQuartersString?: string;
    totalRaised?: string;
  };
}

/**
 * Active company card item component that displays individual company information
 * 
 * This is the current active implementation for individual company cards in the grid view.
 * It replaces the deprecated CompanyCard component.
 * 
 * @param props - Component props
 * @param props.company - Company data to display
 * @returns Rendered component
 * 
 * @see {@link src/components/ui/cardCompanies/CompanyCard.jsx} - DEPRECATED implementation this replaces
 */
const CompanyCardItem: React.FC<CompanyCardItemProps> = ({ company }) => {
  return (
    <Flex
      direction={"column"}
      rowGap={"16px"}
      w={"100%"}
      p={"20px"}
      bg={"white"}
      borderRadius={"10px"}
      boxShadow={"0px 0px 14px 0px rgba(114, 114, 114, 0.25)"}
    >
      <CompanyCardItemHeader 
        company={{
          company: {
            slug: company.company.slug,
            companyName: company.company.companyName,
            fileLogo: company.company.fileLogo || ""
          },
          tags: company.tags || []
        }} 
      />
      <Text
        fontSize={"21px"}
        fontWeight={"700"}
        lineHeight={"auto"}
        letterSpacing={"2%"}
      >
        {company?.company?.companyName}
      </Text>
      <AcceleratorCardView item={company} />
      <Flex
        flexDir={"column"}
        gap={"16px"}
        flexGrow={"1"}
        fontSize={"14px"}
        fontWeight={"400"}
        lineHeight={"1.35"}
        letterSpacing={"0.28px"}
        whiteSpace={"pre-wrap"}
        justifyContent={"end"}
      >
        {company?.headQuartersString ? (
          <Text className="truncateString">{`HQ: ${company?.headQuartersString}`}</Text>
        ) : (
          <Flex w={"1px"} h={"19px"}></Flex>
        )}
        {company?.yearFounded && company?.yearFounded !== "—" ? (
          <Text>{`Year Founded: ${company?.yearFounded}`}</Text>
        ) : (
          <Flex w={"1px"} h={"19px"}></Flex>
        )}
        {company?.description ? (
          <Text noOfLines={2} minH={"38px"}>
            {company?.description}
          </Text>
        ) : null}
        <CompanyStageItem keyName={"Deal Stage"} value={company?.stage || "—"} />
        <CompanyStageItem
          keyName={"Total Raised"}
          value={company?.totalRaised || "—"}
        />
        <Link
          href={`/companies/${company?.company?.slug}`}
          title={company?.company?.companyName}
          style={{ width: "fit-content" }}
        >
          <CustomElement
            variant={"blueBtn"}
            title={"View Details"}
            customStyle={{ maxW: "141px", w: "141px" }}
            handler={() => {}}
          />
        </Link>
      </Flex>
    </Flex>
  );
};

export default memo(CompanyCardItem);
